import { DatasetDataApi } from "api/DatasetDataApi";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import DatasetTableDefault from "components/dashboard/dataset-table/DatasetTableDefault";
import "components/dashboard/dataset-table/DatasetTable.scss";
import DatasetTableGrouped from "components/dashboard/dataset-table/DatasetTableGrouped";
import DatasetTableTransposed from "components/dashboard/dataset-table/DatasetTableTransposed";
import {
  DatasetTableMode,
  IDataArray,
  IDatasetTotal,
  IFlattenedDataset,
} from "components/dashboard/dataset-table/utils/interfaces";
import { getRowData } from "components/dashboard/dataset-table/utils/utils";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import {
  DatasetFullDefaultConfig,
  HighlightMapped,
  TimeConfigDefaultDefined,
} from "models/dataset-models";
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { CSSProperties, useEffect, useId, useState } from "react";
import { downloadBlobAsCsvFile } from "utils/file-utils";
import { JodaTimeUtils } from "utils/joda-time-utils";
import {
  addToDate,
  ChronoUnitLegacy,
  dateBody,
  formatTime,
  truncateDate,
} from "utils/time-utils";
import { findValue, timeConfigValidation } from "utils/utils";
import * as XLSX from "xlsx-js-style";
import { getValueColors } from "../product-template-table/utils/utils";

const DatasetTable = (props: DatasetFullDefaultConfig) => {
  const { datasets, config, reportOptions, referenceTime, reportMode } = props;
  const tableMode = props.mode || DatasetTableMode.SINGLE;
  const uniqueComponentId = useId().replace(/[^a-zA-Z0-9]/g, "");
  const tableId = useId().replace(/[^a-zA-Z0-9]/g, "");
  const setStatus = useComponentStateCallback(props);
  const configComponent = React.useState<TimeConfigDefaultDefined>({
    alignStartTo: "HOURS" as ChronoUnitLegacy,
    startOffsetUnits: "HOURS" as ChronoUnitLegacy,
    startOffset: 0,
    lagUnits: "HOURS" as ChronoUnitLegacy,
    lagOffset: 0,
    leadUnits: "HOURS" as ChronoUnitLegacy,
    leadOffset: 0,
  });

  const identifiers = React.useRef<Array<string>>([]);
  const dateFormat = config?.dateFormat || ChronoUnitLegacy.MINUTES;

  const [refresh, setRefresh] = useState<boolean | undefined>(undefined);
  const [frequency, setFrequency] = useState<number>(
    config?.startAutoRefreshFrequency && config.startAutoRefreshFrequency > 0
      ? config.startAutoRefreshFrequency * 1000
      : 0
  );

  const [data, setData] = useState<Array<IDataArray>>([]);

  const [datasetsArray, setDatasetsArray] = useState<Array<any>>(
    datasets ? datasets : []
  );

  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  const [loading, setLoading] = useState<boolean>(true);
  const [requesting, setRequesting] = useState<boolean>(false);

  const [allSteps, setAllSteps] = useState<Array<Date>>();
  const [filteredSteps, setFilteredSteps] = useState<number>();

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState(config?.itemsPerPage || 10);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [totals, setTotals] = useState<IDatasetTotal[]>([]);
  const [filteredData, setFilteredData] = useState(data);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let customData = data;
    if (config?.showEmpty === false) {
      customData = data.filter((obj) => {
        const tempObj = structuredClone(obj);
        delete tempObj["date"];
        const allIsEmpty = Object.values(tempObj).every((v) => v === "");
        return !allIsEmpty;
      });
    }

    setFilteredSteps(customData.length);
    setFilteredData(customData.slice(page, page + rows));

    if (config?.totalSize === "ALL") {
      calculateTotals(customData, datasetsArray);
    } else {
      calculateTotals(customData.slice(page, page + rows), datasetsArray);
    }
  }, [data, currentPage, rows]);

  const rowsPerPageOptions = config?.itemsPerPage
    ? [config.itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter(function (x, i, a) {
          return a.indexOf(x) === i;
        })
    : [10, 20, 30];

  const mappedHighlights: Array<HighlightMapped> = datasets
    ? datasets
        .map((ob) => {
          return {
            identifier: ob.identifier,
            highlightRanges: ob.highlightRanges,
            defaultHighlight: ob.defaultHighlight,
          } as HighlightMapped;
        })
        .filter(
          (e) =>
            e.defaultHighlight ||
            (e.highlightRanges && e.highlightRanges.length > 0)
        )
    : [];

  useEffect(() => {
    let tempIdentifiers: string[] = [];

    const extractIdentifiers = (obj: any) => {
      if (obj.identifier) {
        tempIdentifiers.push(obj.identifier);
      }
      if (obj.children) {
        obj.children.forEach((child: any) => extractIdentifiers(child));
      }
    };

    datasets?.forEach((dataset) => extractIdentifiers(dataset));

    if (tempIdentifiers.length > 0) {
      const uniqueIdentifiers = new Set(tempIdentifiers);
      if (uniqueIdentifiers.size !== tempIdentifiers.length) {
        const duplicates = tempIdentifiers.filter(
          (item, index) => tempIdentifiers.indexOf(item) !== index
        );
        setErrorMessage(
          "Error: Duplicate datasets: " + duplicates.join(", ") + "."
        );
        setLoading(false);
        return;
      }
      identifiers.current = Array.from(uniqueIdentifiers);
    }
    truncateStarterDateFromConfig();
    setRefresh(true);
  }, []);

  useEffect(() => {
    if (!loading) {
      getDataInfoRange()
      const minTableWidthElement =
        document.getElementsByClassName("min-table-width")[0];
      if (!config?.transposed && minTableWidthElement) {
        minTableWidthElement.style.minWidth =
          identifiers.current.length * 100 + 100 + "px";
      }

      if (reportOptions && reportMode) {
        const table = document.querySelector(`#${tableId}`);

        if (reportOptions.fontSize && table) {
          table.style.fontSize = reportOptions.fontSize;
        }
      }

      setStatus({ state: "finished" });
    }
  }, [loading]);

  useEffect(() => {
    getStepsCount();
  }, [dateFrom, dateTo]);

  useEffect(() => {
    getDataInfoRange();
  }, [allSteps]);

  useEffect(() => {
    if (refresh) {
      if (config && config.startAutoRefreshFrequency && !reportMode) {
        setFrequency(
          config.startAutoRefreshFrequency > 0
            ? config.startAutoRefreshFrequency * 1000
            : 0
        );
      }
      truncateStarterDateFromConfig();
    }
  }, [refresh]);

  useEffect(() => {
    if (refresh && frequency > 0) {
      const interval = setInterval(() => {
        truncateStarterDateFromConfig();
      }, frequency);
      return () => clearInterval(interval);
    }
  }, [refresh, frequency]);

  useEffect(() => {
    if (isDialogOpen) {
      setFrequency(0);
    }
  }, [isDialogOpen, frequency]);

  const getStepsCount = () => {
    if (dateFrom && dateTo) {
      let { from, to } = compareTwoDates(dateTo, dateFrom);
      let stepSize: number = config
        ? config.stepSize
          ? config.stepSize
          : 1
        : 1;

      let stepUnits: ChronoUnitLegacy = config
        ? config.stepUnits
          ? config.stepUnits
          : ChronoUnitLegacy.HOURS
        : ChronoUnitLegacy.HOURS;

      let stepsArr: Array<Date> = [];

      const convertedUnit =
        JodaTimeUtils.convertLegacyChronoUnitToJoda(stepUnits);
      from = JodaTimeUtils.CeilingTo(from, convertedUnit);

      while (from <= to) {
        stepsArr.push(from);
        from = addToDate(from, stepSize, stepUnits);
      }

      setAllSteps([
        ...(config?.sort === "ASC" ? stepsArr : stepsArr.reverse()),
      ]);
    }
  };

  const compareTwoDates = (first: Date, second: Date) => {
    if (first > second) {
      return { from: second, to: first };
    } else {
      return { from: first, to: second };
    }
  };

  const truncateStarterDateFromConfig = () => {
    let { from, to } = getTimeRange();
    if (
      new Date(from).getTime() !== dateFrom?.getTime() ||
      new Date(to).getTime() !== dateTo?.getTime()
    ) {
      setDateFrom(new Date(from));
      setDateTo(new Date(to));
    }
    if (!allSteps) {
      getStepsCount();
    }
  };

  const getDataInfoRange = async () => {
    if (requesting) {
      return;
    }

    if (identifiers.current.length === 0 || !(allSteps ? allSteps.length : 0)) {
      return;
    }

    setRequesting(true);
    await DatasetDataApi.getDataInfoRange(
      identifiers.current,
      dateFrom?.toISOString()!!,
      dateTo?.toISOString()
    ).then(({ error, data: dataRes }) => {
      setRequesting(false);
      if (loading) {
        setLoading(false);
      }

      if (error) {
        console.error("Error");
        setStatus({ state: "error", error: error.message });
        return;
      }

      if (dataRes) {
        if (config?.transposed) {
          setData(mapDataTransposed(dataRes));
        } else {
          updateDatasetsArrayWithInfo(dataRes);
          setData(mapData(dataRes));
        }
      }
    });
  };

  const mapDataTransposed = (
    data: Array<DatasetDataInfoResponse>,
    page?: number
  ) => {
    const dates = getPageDates(page || currentPage);

    return mapAndTransformDataResponse(dates, data);
  };

  const getPageDates = (pageNumber: number): Array<string> => {
    if (allSteps) {
      const { from, to } = calculatePageDates(pageNumber);
      return allSteps.slice(from, to + 1).map((e) => e.toISOString());
    }
    return [];
  };

  const updateDatasetsArrayWithInfo = async (
    data: Array<DatasetDataInfoResponse>
  ) => {
    const updateObject = (obj: any) => {
      if (obj.identifier) {
        const found = data.find((a) => a.identifier === obj.identifier);
        if (found) {
          const updatedObj: any = {
            ...obj,
            data: found.data,
            title: found.title,
            unit: found.unit,
            stringValueMapper: found.stringValueMapper,
            defaultHighlight: obj.defaultHighlight,
            highlightRanges: obj.highlightRanges,
            decimalPlaces:
              obj.decimalPlaces != null
                ? obj.decimalPlaces
                : found.decimalPlaces ?? 2,
          };
          return updatedObj;
        }
      }
      if (obj.children) {
        obj.children = obj.children.map((child: any) => updateObject(child));
      }
      return obj;
    };
    setDatasetsArray(datasetsArray.map(updateObject));
  };

  const getInfoRange = async () => {
    const { from, to } = calculatePageDates(-1);
    let fromStep = allSteps ? allSteps[from] : new Date();
    let toStep = allSteps ? allSteps[to] : new Date();
    if (fromStep > toStep) {
      [fromStep, toStep] = [toStep, fromStep];
    }
    return await DatasetDataApi.getDataInfoRange(
      identifiers.current,
      fromStep.toISOString() as string,
      toStep.toISOString() as string
    );
  };

  const generateDataExportCSV = async () => {
    let headerLine = "Date";
    datasetsArray.forEach((ob) => {
      if (ob.unit && ob.unit !== " ") {
        if (ob.overriddenTitle) {
          headerLine =
            headerLine + ',"' + ob.overriddenTitle + ", " + ob.unit + '"';
        } else if (ob.title) {
          headerLine = headerLine + ',"' + ob.title + ", " + ob.unit + '"';
        } else {
          headerLine = headerLine + ',"' + ob.identifier + ", " + ob.unit + '"';
        }
      } else {
        if (ob.overriddenTitle) {
          headerLine = headerLine + "," + ob.overriddenTitle;
        } else if (ob.title) {
          headerLine = headerLine + "," + ob.title;
        } else {
          headerLine = headerLine + "," + ob.identifier;
        }
      }
    });

    const { error, data } = await getInfoRange();
    if (error) {
      console.error("Error");
      return;
    }

    let bodyLine = "";
    if (data) {
      mapData(data).forEach((obj) => {
        let newLine = "";
        Object.entries(obj).forEach(([key, value]) => {
          if (key === "date") {
            newLine = dateBody(value as string, dateFormat) + newLine;
          } else {
            newLine += ",";
            newLine += value && value !== " " ? value : "-";
          }
        });
        newLine += "\n";
        bodyLine += newLine;
      });
    }

    return headerLine + "\n" + bodyLine;
  };

  const generateDataExportCSVTransposed = async () => {
    const headerLine = [""];

    allSteps?.forEach((step) => {
      headerLine.push(formatTime(step));
    });

    const { error, data: dataNew } = await getInfoRange();
    if (error) {
      console.error("Error");
      return;
    }

    let bodyLine = "";
    if (dataNew) {
      dataNew.forEach((obj) => {
        let newLine = `"${obj.title}, ${obj.unit}",`;
        const dp = obj.decimalPlaces || 2;

        const dataMap = new Map(obj.data.map((item) => [item.key, item.value]));

        newLine += allSteps
          ?.map((step) =>
            dataMap.has(step.toISOString())
              ? Number(dataMap.get(step.toISOString())).toFixed(dp)
              : "-"
          )
          .join(",");

        newLine += "\n";
        bodyLine += newLine;
      });
    }

    return headerLine.join(",") + "\n" + bodyLine;
  };

  const handleDataExport = async () => {
    setRequesting(true);

    try {
      const dataExportFn = config?.transposed
        ? generateDataExportCSVTransposed
        : generateDataExportCSV;
      const csvData = await dataExportFn();

      downloadBlobAsCsvFile(csvData as BlobPart, "datasetTable");
    } catch (error) {
      console.error("Error occurred during data export:", error);
    } finally {
      setRequesting(false);
    }
  };

  const calculatePageDates = (pageNumber: number) => {
    const stepsArr = allSteps || [];
    const itemsPerPage = rows;
    const from = pageNumber === -1 ? 0 : pageNumber * itemsPerPage;
    const to =
      pageNumber === -1
        ? stepsArr.length - 1
        : Math.min(from + itemsPerPage - 1, stepsArr.length - 1);

    return { from, to };
  };

  const mapData = (data: Array<DatasetDataInfoResponse>) => {
    let dates = getAllDates(data);

    return mapAndReplaceDateProperties(dates, data);
  };

  const getAllDates = (data: Array<any>): Array<any> => {
    let allTimestamps: Array<any> = [];
    let tempColumnObj: any = {};

    data.forEach((o) => {
      tempColumnObj[o.identifier] = "";
    });
    if (allSteps) {
      allTimestamps = allSteps.map((obj) => {
        return {
          ...tempColumnObj,
          date: obj.toISOString().split(".")[0] + "Z",
        };
      });
    }

    return allTimestamps;
  };

  const mapAndReplaceDateProperties = (
    dates: Array<any>,
    data: Array<DatasetDataInfoResponse>
  ) => {
    return dates.map((obj) => {
      const tempObj = { ...obj };
      Object.keys(obj).forEach((ob) => {
        const objTime = new Date(obj.date).getTime();
        if (ob !== "date") {
          const temp = data.find((res) => res.identifier === ob)?.data || [];
          if (temp.length > 0) {
            const ansValue = findValue(temp, objTime);
            if (ansValue || ansValue === 0) {
              tempObj[ob] = ansValue;
            }
          }
        }
      });

      return tempObj;
    });
  };

  const mapAndTransformDataResponse = (
    dates: Array<string>,
    dataRes: Array<DatasetDataInfoResponse>
  ) => {
    return dataRes.map((res) => {
      const conf = datasets?.find((e) => e.identifier === res.identifier);

      const tempObj: any = {
        title: res.title,
        unit: res.unit,
        identifier: res.identifier,
        decimalPlaces: conf?.decimalPlaces || res.decimalPlaces,
        overriddenTitle: conf?.overriddenTitle,
      };

      const decimalPlaces = res.decimalPlaces || 2;

      for (const d of res.data) {
        const date = new Date(d.key);
        date.setMilliseconds(0);
        const tempKey = date.toISOString();

        if (dates.includes(tempKey)) {
          tempObj[tempKey] = d.value.toFixed(decimalPlaces);
        }
      }

      return tempObj;
    });
  };

  const getTimeRange = () => {
    if (config) {
      configComponent[0] = timeConfigValidation(config);
    } else {
      configComponent[0] = timeConfigValidation({});
    }
    let tempFrom: Date, tempTo: Date;

    const startingDate = referenceTime ? new Date(referenceTime) : new Date();

    let tempDate = truncateDate(startingDate, config?.alignStartTo);
    tempFrom = tempTo = addToDate(
      tempDate,
      configComponent[0].startOffset,
      configComponent[0].startOffsetUnits
    );
    tempFrom = addToDate(
      tempFrom,
      configComponent[0].lagOffset * -1,
      configComponent[0].lagUnits
    );
    tempTo = addToDate(
      tempTo,
      configComponent[0].leadOffset,
      configComponent[0].leadUnits
    );
    return compareTwoDates(tempFrom, tempTo);
  };

  const setCellStyle = (
    identifier: string,
    value: number | undefined
  ): CSSProperties | undefined => {
    const id = mappedHighlights.find((e) => e.identifier === identifier);

    const [color, bgColor] = getValueColors(value, {
      highlightRanges: id?.highlightRanges,
      defaultHighlightColor: id?.defaultHighlight,
    });

    return { color, backgroundColor: bgColor };
  };

  const calculateTotals = (data: any[], datasetsArray: any[]) => {
    const calculateTotal = (dataset: any) => {
      if (!dataset.identifier) {
        return null;
      }

      let tempTotal = 0;
      let totalActualValues = 0;
      if (dataset.showTotal || config?.showTotal) {
        data.map((element) => {
          const value = element[dataset.identifier];
          if (typeof value === "number") {
            tempTotal += value;
            totalActualValues++;
          }
        });
      }
      if (totalActualValues > 0) {
        switch (dataset.totalType || config?.totalType) {
          case "SUM":
            return {
              identifier: dataset.identifier,
              value: tempTotal.toFixed(dataset.decimalPlaces),
              tooltip: "Sum",
            };
          case "AVG":
            const average = tempTotal / totalActualValues;
            return {
              identifier: dataset.identifier,
              value: average.toFixed(dataset.decimalPlaces),
              tooltip: "Average",
            };
          default:
            return { identifier: dataset.identifier };
        }
      } else {
        return { identifier: dataset.identifier };
      }
    };

    const calculateTotalsRecursive = (dataset: any) => {
      if (dataset.children) {
        return dataset.children.flatMap(calculateTotalsRecursive);
      } else {
        return calculateTotal(dataset);
      }
    };

    const allTotals = datasetsArray
      .flatMap(calculateTotalsRecursive)
      .filter((total) => total !== null);

    setTotals(allTotals);
  };

  const flattenDatasets = (datasetsArray: any[]) => {
    let datasets: IFlattenedDataset[] = [];
    const extractDatasetData = (obj: any, parentHeaders: string = "") => {
      const currentHeaders = obj.header
        ? parentHeaders
          ? `${parentHeaders}: ${obj.header}`
          : obj.header
        : parentHeaders;
      if (obj.children) {
        obj.children.forEach((child: any) =>
          extractDatasetData(child, currentHeaders)
        );
      } else {
        const finalTitle = currentHeaders
          ? `${currentHeaders}: ${obj.overriddenTitle || obj.title}`
          : `${obj.overriddenTitle || obj.title}`;
        datasets.push({
          title: finalTitle,
          identifier: obj.identifier,
          unit: obj.unit,
          decimalPlaces: obj.decimalPlaces,
        });
      }
    };
    datasetsArray.forEach((dataset) => extractDatasetData(dataset));
    return datasets;
  };

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const datasets = ["Date", ...flattenDatasets(datasetsArray)];

    const rowData = getRowData(data, datasets, dateFormat);

    const headersTitles = datasets.map((dataset) => {
      if (typeof dataset === "string") {
        return "Date";
      } else {
        if (dataset.unit && dataset.unit !== " ") {
          return dataset.title + ", " + dataset.unit;
        } else {
          return dataset.title;
        }
      }
    });

    const ws_data = [
      headersTitles,
      ...rowData.map((row) =>
        datasets.map((header) =>
          typeof header === "string" ? row.Date : row[header.identifier]
        )
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(ws_data);

    XLSX.utils.book_append_sheet(wb, ws, "Data");

    XLSX.writeFile(wb, "data.xlsx");
  };

  return (
    <div className={"dataset-table-bull-window"} id={uniqueComponentId}>
      <div className={"config-container non-printable"}>
        <div className={"date-inputs-container"}>
          <div className={"calendar-container"}>
            <b style={{ padding: "0 0 0 5px" }}>From</b>
            <CalendarWrapper
              value={dateFrom}
              onChange={(e) => {
                setRefresh(false);
                setDateFrom(e);
              }}
              dateFormat={"yy-mm-dd"}
            />
          </div>
          <div className={"calendar-container"}>
            <b style={{ padding: "0 0 0 5px" }}>To</b>
            <CalendarWrapper
              value={dateTo}
              onChange={(e) => {
                setRefresh(false);
                setDateTo(e);
              }}
              dateFormat={"yy-mm-dd"}
            />
          </div>
        </div>
        <div className={"button-container"}>
          {!refresh && (
            <Button
              icon={"pi pi-history"}
              onClick={() => {
                setRefresh(true);
                if (currentPage !== 0) {
                  setCurrentPage(0);
                }
                if (page !== 1) {
                  setPage(1);
                }
              }}
              loading={requesting}
              disabled={refresh}
            />
          )}
          <div className={"button-to-left"}>
            {tableMode === DatasetTableMode.SINGLE ? (
              <Button
                icon={"pi pi-file"}
                onClick={() => handleDataExport()}
                tooltip={"Export"}
                loading={requesting}
              />
            ) : (
              <Button
                type="button"
                icon="pi pi-file-excel"
                onClick={exportExcel}
                className="p-button-success mr-2"
                data-pr-tooltip="XLS"
              />
            )}

            <Button
              icon={"pi pi-refresh"}
              onClick={() => getDataInfoRange()}
              loading={requesting}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className={"loading-screen"}>
          <ProgressSpinner style={{ overflow: "hidden" }} />
        </div>
      ) : errorMessage ? (
        <div className="font-bold text-red-500">{errorMessage}</div>
      ) : (
        <div className={"table-with-paginator"}>
          <div id={tableId}>
            {config?.transposed ? (
              <DatasetTableTransposed
                config={config}
                reportOptions={reportOptions}
                reportMode={reportMode}
                data={filteredData}
                getPageDates={getPageDates}
                currentPage={currentPage}
                highlightRanges={mappedHighlights}
              />
            ) : tableMode === DatasetTableMode.GROUPED ? (
              <DatasetTableGrouped
                config={config}
                datasetsArray={datasetsArray}
                data={filteredData}
                totals={totals}
                setIsDialogOpen={setIsDialogOpen}
              />
            ) : (
              <DatasetTableDefault
                config={config}
                datasetsArray={datasetsArray}
                data={filteredData}
                getCellStyle={setCellStyle}
                totals={totals}
                setIsDialogOpen={setIsDialogOpen}
                dateFrom={dateFrom}
                dateTo={dateTo}
              />
            )}
          </div>
          <Paginator
            first={page}
            className={"non-printable"}
            rows={rows}
            onPageChange={(e) => {
              if (requesting) {
                return;
              }

              setCurrentPage(e.page);
              setPage(e.first);
              setRows(e.rows);
            }}
            totalRecords={filteredSteps ? filteredSteps : 0}
            template={
              "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            }
            currentPageReportTemplate={
              "Showing {first} to {last} of {totalRecords}"
            }
            rowsPerPageOptions={rowsPerPageOptions}
          />
        </div>
      )}
    </div>
  );
};

export default DatasetTable;
